import axios from "axios";

const isDev = process.env.NODE_ENV === "development";

const API_BASE_URL_V2 =
  process.env.REACT_APP_API_BASE_URL_V2 ||
  (isDev ? "http://localhost:5000/api/v2" : "https://api.ducktail.in/api/v2");

const API_BASE_URL_V1 =
  process.env.REACT_APP_API_BASE_URL_V1 ||
  (isDev ? "http://localhost:5000/api/v1" : "https://api.ducktail.in/api/v1");

const API_BASE_URL_ENQUIRY =
  process.env.REACT_APP_API_BASE_URL_ENQUIRY ||
  (isDev ? "http://localhost:3001/api/v2" : "https://adminapi.ducktail.in/api/v2");

const API_BASE_URL_ADMIN =
  process.env.REACT_APP_API_BASE_URL_ADMIN ||
  (isDev ? "http://localhost:3001/api/v2" : "https://adminapi.ducktail.in/api/v2");

// ─── Axios Instances ──────────────────────────────────────────────────────────
const axiosInstance = axios.create({
  baseURL: API_BASE_URL_V2,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

const axiosInstancev1 = axios.create({
  baseURL: API_BASE_URL_V1,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

const axiosEnquiryInstance = axios.create({
  baseURL: API_BASE_URL_ENQUIRY,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

const axiosInstancev2 = axios.create({
  baseURL: API_BASE_URL_ADMIN,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

// ─── Token Helper ─────────────────────────────────────────────────────────────
const getAuthToken = () => {
  const isConsultantRoute = window.location.pathname.startsWith("/consultant");

  if (isConsultantRoute) {
    return (
      localStorage.getItem("consultantToken") ||
      localStorage.getItem("builderToken") ||
      localStorage.getItem("customerToken") ||
      localStorage.getItem("agentToken")
    );
  }

  return (
    localStorage.getItem("builderToken") ||
    localStorage.getItem("customerToken") ||
    localStorage.getItem("agentToken") ||
    localStorage.getItem("consultantToken")
  );
};

// ─── Request Interceptor ──────────────────────────────────────────────────────
const attachAuthInterceptor = (instance) => {
  instance.interceptors.request.use(
    (config) => {
      const token = getAuthToken();
      if (token && !config.headers["Authorization"]) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
};

// ─── Session-Expiry Guard ─────────────────────────────────────────────────────
// When multiple API calls fail simultaneously due to an expired/invalid JWT,
// this flag ensures we show exactly ONE toast and redirect only once.
let isHandlingExpiry = false;

const handleSessionExpiry = () => {
  if (isHandlingExpiry) return;
  isHandlingExpiry = true;

  // Clear every known token
  localStorage.removeItem("builderToken");
  localStorage.removeItem("customerToken");
  localStorage.removeItem("agentToken");
  localStorage.removeItem("consultantToken");
  sessionStorage.clear();

  // Reset Zustand auth state (lazy import avoids circular dependency)
  import("../store/useAuthStore")
    .then(({ default: useAuthStore }) => {
      useAuthStore.setState({ user: null, userType: null, token: null });
    })
    .catch(() => {/* store not available yet — skip */});

  // Show ONE friendly toast (react-hot-toast deduplicates by id)
  import("react-hot-toast").then(({ toast }) => {
    toast.error("Your session has expired. Please log in again.", {
      id: "session-expired",   // prevents duplicate toasts
      duration: 4000,
    });
  });

  // Determine the correct login page then redirect after a short delay
  const path = window.location.pathname;
  let loginPath = "/";
  if (path.includes("/builder"))    loginPath = "/builder/login";
  else if (path.includes("/agent")) loginPath = "/agent/login";
  else if (path.includes("/customer")) loginPath = "/customer/login";
  else if (path.includes("/consultant")) loginPath = "/consultant/login";

  setTimeout(() => {
    isHandlingExpiry = false; // reset so future fresh logins work normally
    window.location.href = loginPath;
  }, 1500);
};

// ─── Response Interceptor ─────────────────────────────────────────────────────
const attachResponseInterceptor = (instance) => {
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;
      const bodyMsg = (
        error.response?.data?.message ||
        error.response?.data?.error ||
        ""
      ).toLowerCase();

      // Treat as session expiry if:
      //  • 401 Unauthorized  (standard token-expired code)
      //  • Backend explicit messages: "Forbidden - Invalid JWT token", "jwt expired", etc.
      //  • 403 Forbidden with a JWT/token/invalid/forbidden keyword in the body
      const isExpiredJwt =
        status === 401 ||
        bodyMsg.includes("invalid jwt") ||
        bodyMsg.includes("jwt expired") ||
        bodyMsg.includes("token expired") ||
        bodyMsg.includes("invalid token") ||
        (status === 403 &&
          (bodyMsg.includes("jwt") ||
           bodyMsg.includes("token") ||
           bodyMsg.includes("invalid") ||
           bodyMsg.includes("forbidden")));

      if (isExpiredJwt) {
        handleSessionExpiry();
        // Return an unresolved promise to silence downstream catch() blocks in components,
        // preventing them from displaying multiple "Invalid JWT token" toasts during the redirect.
        return new Promise(() => {});
      }

      return Promise.reject(error);
    }
  );
};

// ─── Attach Interceptors ──────────────────────────────────────────────────────
attachAuthInterceptor(axiosInstance);
attachAuthInterceptor(axiosInstancev1);
attachAuthInterceptor(axiosEnquiryInstance);
attachAuthInterceptor(axiosInstancev2);

attachResponseInterceptor(axiosInstance);
attachResponseInterceptor(axiosInstancev1);
attachResponseInterceptor(axiosEnquiryInstance);
attachResponseInterceptor(axiosInstancev2);

// ─── Exports ──────────────────────────────────────────────────────────────────
export {
  axiosInstance,
  axiosInstancev1,
  axiosEnquiryInstance,
  axiosInstancev2,
};